import React, { useEffect, useRef, useState } from 'react';

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz';
const pick = () => ALPHABET[Math.floor(Math.random() * ALPHABET.length)];

/**
 * Cycles through `words`, scrambling letters between each transition.
 * Pauses for `holdMs`, then scrambles for `scrambleMs` into the next word.
 * Respects prefers-reduced-motion (plain swap, no scramble).
 * Ported from Breww landing — no anime.js dep.
 */
const ScrambleWord = ({
  words,
  holdMs = 2400,
  scrambleMs = 900,
  className = '',
  minWidth = '',
}) => {
  const [display, setDisplay] = useState(words[0] || '');
  const idxRef = useRef(0);
  const rafRef = useRef(0);
  const timerRef = useRef(0);

  useEffect(() => {
    if (!words || words.length < 2) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const scheduleNext = () => {
      timerRef.current = window.setTimeout(() => {
        const nextIdx = (idxRef.current + 1) % words.length;
        const from = words[idxRef.current];
        const to = words[nextIdx];

        if (reduce) {
          idxRef.current = nextIdx;
          setDisplay(to);
          scheduleNext();
          return;
        }

        const start = performance.now();
        const len = Math.max(from.length, to.length);
        const tick = (now) => {
          const t = Math.min(1, (now - start) / scrambleMs);
          let out = '';
          for (let i = 0; i < len; i++) {
            const locked = i / len < t;
            out += locked ? (to[i] ?? '') : pick();
          }
          setDisplay(out);
          if (t < 1) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            idxRef.current = nextIdx;
            setDisplay(to);
            scheduleNext();
          }
        };
        rafRef.current = requestAnimationFrame(tick);
      }, holdMs);
    };

    scheduleNext();
    return () => {
      window.clearTimeout(timerRef.current);
      cancelAnimationFrame(rafRef.current);
    };
  }, [words, holdMs, scrambleMs]);

  return (
    <span
      className={className}
      aria-live="off"
      style={{ display: 'inline-block', minWidth: minWidth || undefined }}
    >
      {display}
    </span>
  );
};

export default ScrambleWord;
